import {
  ChangeEventHandler,
  Dispatch,
  FocusEventHandler,
  KeyboardEventHandler,
  SetStateAction,
} from 'react'
import Input, { InputProps } from '../../../../../../Components/Form/Inputs/Generic/Input'
import { capitalise } from '../../../../../../Components/Form/Inputs/Validation/Utilities/stringHelpers'

interface NameInputProps
  extends Omit<
    InputProps,
    | 'value'
    | 'type'
    | 'required'
    | 'minLength'
    | 'maxLength'
    | 'onBlur'
    | 'forwardedRef'
    | 'ref'
    | 'pattern'
    | 'patternMismatch'
  > {
  name: string
  setName: Dispatch<SetStateAction<string>>
}

const NameInput = ({
  name,
  setName,
  fieldName,
  onChange: _onChange,
  ...props
}: NameInputProps): JSX.Element => {
  const capitalisedFieldName = fieldName ? capitalise(fieldName) : 'Name'
  const trimOnBlur: FocusEventHandler<HTMLInputElement> = ({ target }) =>
    setName(target.value.trim())
  const trimOnEnter: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key !== 'Enter') return

    setName(e.currentTarget.value.trim())
  }
  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    _onChange && _onChange(event)
    setName(event.target.value)
  }
  // Regex blocks leading and trailing spaces; punctuation other than `-` and `'`; and digits.
  // The `\p{L}` unicode letter expression, as used in RedBox, is not supported by JS
  const patternRegex =
    '[^\\d\\s_!@£$%&\\*\\(\\)"\\.:;\\|\\\\\\/<>`~,§±\\+=€\\^\\?\\{\\}\\[\\]]+' +
    '\\s*' +
    '[^\\d\\s_!@£$%&\\*\\(\\)"\\.:;\\|\\\\\\/<>`~,§±\\+=€\\^\\?\\{\\}\\[\\]]+'
  return (
    <Input
      type='text'
      required
      minLength={2}
      maxLength={35}
      pattern={patternRegex}
      errorMessageOverrides={{
        patternMismatch: `${capitalisedFieldName} must not contain numbers or special punctuation`,
      }}
      title={
        `${capitalisedFieldName} must contain letters, apostrophes or hyphens. Numbers, other ` +
        'punctuation and leading and trailing spaces are not permitted'
      }
      fieldName={fieldName}
      onBlur={trimOnBlur}
      onKeyDown={trimOnEnter}
      value={name}
      onChange={onChange}
      {...props}
    />
  )
}

export default NameInput
